import { IconButton, Tooltip } from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import { tooltipSxElevated } from '../../components/shared/jernyTooltipStyling';
import { LINE_HEIGHT_OPTIONS, LINE_HEIGHT_DEFAULT } from '../Core/LineHeight/lineHeightConfig';
import bodyEditorEngine from '../Core/BodyEditorEngine';

/** Available font families — empty string = default (inherit). */
const FONTS = ['', 'Arial', 'Georgia', 'Lucida Console', 'Times New Roman', 'Courier New'];

/** Available font sizes in px — empty string = default (inherit). */
const SIZES = ['', '12', '14', '16', '18', '20', '24'];

/** Paragraph style options — value is heading level (1-3) or 0 for normal text. */
const PARA_STYLES = [
    { label: 'Normal',    value: 0 },
    { label: 'Heading 1', value: 1 },
    { label: 'Heading 2', value: 2 },
    { label: 'Heading 3', value: 3 },
];

/**
 * BodyEditorToolbar — formatting buttons for the WYSIWYG body editor.
 * @param {object} props
 * @param {import('@tiptap/react').Editor} props.editor - tiptap editor instance
 */
export default function BodyEditorToolbar({ editor }) {
    if (!editor) return null;

    /**
     * Returns sx for a toolbar button — highlighted when the format is active.
     * @param {boolean} active
     */
    const btnSx = (active) => ({
        color: active ? '#4cceac' : '#7fb3d3',
        borderRadius: '4px',
        border: `0.5px solid ${active ? '#4cceac' : 'transparent'}`,
        p: '4px',
        '&:hover': { color: '#a3d1ff', borderColor: '#294766' },
    });

    /** Current line-height of the block at cursor. */
    const currentLineHeight =
        editor.getAttributes('paragraph').lineHeight ??
        editor.getAttributes('heading').lineHeight ??
        LINE_HEIGHT_DEFAULT;

    /** Current font family of the selection. */
    const currentFont = editor.getAttributes('textStyle').fontFamily ?? '';

    /** Current font size of the selection (stripped of 'px'). */
    const currentSize = editor.getAttributes('textStyle').fontSize?.replace('px', '') ?? '';

    /** Current paragraph style value (0 = normal, 1-3 = heading level). */
    const currentParaStyle = editor.isActive('heading', { level: 1 }) ? 1
        : editor.isActive('heading', { level: 2 }) ? 2
        : editor.isActive('heading', { level: 3 }) ? 3
        : 0;

    /**
     * Applies paragraph style from dropdown.
     * @param {React.ChangeEvent<HTMLSelectElement>} e
     */
    const handleParaStyle = (e) => {
        const level = parseInt(e.target.value, 10);
        if (level === 0) {
            editor.chain().focus().setParagraph().run();
        } else {
            editor.chain().focus().toggleHeading({ level }).run();
        }
    };

    /**
     * Applies line-height to the current selection.
     * @param {React.ChangeEvent<HTMLSelectElement>} e
     */
    const handleLineHeight = (e) => {
        editor.chain().focus().setLineHeight(parseFloat(e.target.value)).run();
    };

    /**
     * Applies font family to the current selection.
     * @param {React.ChangeEvent<HTMLSelectElement>} e
     */
    const handleFontFamily = (e) => {
        if (e.target.value) {
            editor.chain().focus().setFontFamily(e.target.value).run();
        } else {
            editor.chain().focus().unsetFontFamily().run();
        }
    };

    /**
     * Applies font size to the current selection.
     * @param {React.ChangeEvent<HTMLSelectElement>} e
     */
    const handleFontSize = (e) => {
        editor.chain().focus().setFontSize(e.target.value ? `${e.target.value}px` : null).run();
    };

    return (
        <div className="be-toolbar">

            {/* ── Row 1: Save, Font, Size, Bold, Italic, Indent, LineHeight, Undo/Redo ── */}
            <div className="be-toolbar-row">
                {/* Save */}
                <Tooltip title="Save (Ctrl+S)" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(false)}
                        onMouseDown={async (e) => {
                            e.preventDefault();
                            const html = editor.getHTML();
                            await bodyEditorEngine.save(html);
                        }}
                    >
                        <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                <div className="be-toolbar-sep" />

                {/* Font family */}
                <Tooltip title="Font family" componentsProps={tooltipSxElevated} placement="top">
                    <select className="be-toolbar-select be-toolbar-select-font" value={currentFont} onChange={handleFontFamily}>
                        {FONTS.map((f) => (
                            <option key={f} value={f}>{f || 'Default'}</option>
                        ))}
                    </select>
                </Tooltip>

                {/* Font size */}
                <Tooltip title="Font size" componentsProps={tooltipSxElevated} placement="top">
                    <select className="be-toolbar-select be-toolbar-select-size" value={currentSize} onChange={handleFontSize}>
                        {SIZES.map((s) => (
                            <option key={s} value={s}>{s || 'Size'}</option>
                        ))}
                    </select>
                </Tooltip>

                <div className="be-toolbar-sep" />

                {/* Bold */}
                <Tooltip title="Bold (Ctrl+B)" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(editor.isActive('bold'))}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
                    >
                        <FormatBoldIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                {/* Italic */}
                <Tooltip title="Italic (Ctrl+I)" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(editor.isActive('italic'))}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
                    >
                        <FormatItalicIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                <div className="be-toolbar-sep" />

                {/* Indent */}
                <Tooltip title="Indent (Tab)" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(false)}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().indent().run(); }}
                    >
                        <FormatIndentIncreaseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                {/* Outdent */}
                <Tooltip title="Outdent (Shift+Tab)" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(false)}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().outdent().run(); }}
                    >
                        <FormatIndentDecreaseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                <div className="be-toolbar-sep" />

                {/* Line height */}
                <Tooltip title="Line spacing" componentsProps={tooltipSxElevated} placement="top">
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormatLineSpacingIcon sx={{ fontSize: 14, color: '#7fb3d3', mr: '2px' }} />
                        <select className="be-toolbar-select be-toolbar-select-lh" value={currentLineHeight} onChange={handleLineHeight}>
                            {LINE_HEIGHT_OPTIONS.map((v) => (
                                <option key={v} value={v}>{v}</option>
                            ))}
                        </select>
                    </span>
                </Tooltip>

                <div className="be-toolbar-sep" />

                {/* Undo */}
                <Tooltip title="Undo (Ctrl+Z)" componentsProps={tooltipSxElevated} placement="top">
                    <span>
                        <IconButton
                            size="small"
                            sx={btnSx(false)}
                            disabled={!editor.can().undo()}
                            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().undo().run(); }}
                        >
                            <UndoIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </span>
                </Tooltip>

                {/* Redo */}
                <Tooltip title="Redo (Ctrl+Y)" componentsProps={tooltipSxElevated} placement="top">
                    <span>
                        <IconButton
                            size="small"
                            sx={btnSx(false)}
                            disabled={!editor.can().redo()}
                            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().redo().run(); }}
                        >
                            <RedoIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </div>

            {/* ── Row 2: Paragraph style, H1/H2/H3, Lists ── */}
            <div className="be-toolbar-row">
                {/* Paragraph style */}
                <Tooltip title="Paragraph style (Ctrl+Alt+0-3)" componentsProps={tooltipSxElevated} placement="top">
                    <select className="be-toolbar-select be-toolbar-select-para" value={currentParaStyle} onChange={handleParaStyle}>
                        {PARA_STYLES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                </Tooltip>

                <div className="be-toolbar-sep" />

                {/* H1 */}
                <Tooltip title="Page header (H1)" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(editor.isActive('heading', { level: 1 }))}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 1 }).run(); }}
                    >
                        <span style={{ fontSize: 11, fontWeight: 700 }}>H1</span>
                    </IconButton>
                </Tooltip>

                {/* H2 */}
                <Tooltip title="Section header (H2)" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(editor.isActive('heading', { level: 2 }))}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run(); }}
                    >
                        <span style={{ fontSize: 11, fontWeight: 700 }}>H2</span>
                    </IconButton>
                </Tooltip>

                {/* H3 */}
                <Tooltip title="Sub header (H3)" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(editor.isActive('heading', { level: 3 }))}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 3 }).run(); }}
                    >
                        <span style={{ fontSize: 11, fontWeight: 700 }}>H3</span>
                    </IconButton>
                </Tooltip>

                <div className="be-toolbar-sep" />

                {/* Bullet list */}
                <Tooltip title="Bullet list" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(editor.isActive('bulletList'))}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
                    >
                        <FormatListBulletedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                {/* Numbered list */}
                <Tooltip title="Numbered list" componentsProps={tooltipSxElevated} placement="top">
                    <IconButton
                        size="small"
                        sx={btnSx(editor.isActive('orderedList'))}
                        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
                    >
                        <FormatListNumberedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </div>

        </div>
    );
}
